import { ChangeEvent, useState, useMemo, useEffect } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useYupErrors, useYupSchemas } from "../../../../../../hooks/useYupValidation";
import { DespatchShippingItem } from "../../../../../../models/despatch";
import { ValueOf } from "../../../../../../types";

type Key = keyof DespatchShippingItem;

type FieldInputName = {
  [key in keyof DespatchShippingItem]: Key;
};

export const useShippingItemsMaintenance = () => {
  const { t: translate } = useTranslation();
  const [fields, setFields] = useState<DespatchShippingItem>({} as DespatchShippingItem);
  const FieldName: FieldInputName = useMemo(
    () =>
      ({
        type: "type",
        volume: "volume",
        description: "description",
        length: "length",
        width: "width",
        height: "height",
        weight: "weight",
      } as FieldInputName),
    []
  );

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const onPopulateFields = (data: typeof fields) => {
    setFields(data);
  };

  const onResetFields = () => {
    setFields({} as DespatchShippingItem);
    setHasFormBeenSubmitted(false);
    setErrors([]);
  };

  const onUpdateField = (key: Key, value: ValueOf<DespatchShippingItem>) => {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onUpdateFieldEvent = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined
  ) => {
    if (!event || !event.target.name) return;
    const value = Number(event.target.value);

    onUpdateField(event.target.name as keyof DespatchShippingItem, value);
  };

  const { fieldHasError, getErrorByField } = useYupErrors<DespatchShippingItem>(errors);
  const { stringRequired, numberRequired } = useYupSchemas(translate);

  const schema = yup.object().shape({
    type: stringRequired,
    description: stringRequired,
    volume: numberRequired,
  });

  const validateFields = (data: typeof fields) => {
    try {
      if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
      schema.validateSync(data, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      validateFields(fields);
    }
  }, [hasFormBeenSubmitted, fields]);

  return {
    fields,
    FieldName,
    onUpdateField,
    onUpdateFieldEvent,
    onPopulateFields,
    onResetFields,
    hasFormBeenSubmitted,
    fieldHasError,
    getErrorByField,
    validateFields,
  };
};
